import type { OfficialLinkBtnProps } from "$lib/components/ui/OfficialLinks.svelte";
import { SHORT_SITE_URL } from "$lib/statics";
import type { Component } from "svelte";
import IconEarth from '~icons/mdi/earth';
import IconFacebook from '~icons/mdi/facebook';
import IconInstagram from '~icons/mdi/instagram';
import IconWeb from '~icons/mdi/web';

export interface OfficialLinksParams {
    websiteUrl?: string | null;
    facebookId?: string | null;
    instagramId?: string | null;
    unescoId?: number | null;
}

export type OfficialLinkInputMap = {
    officialWebsite: string;
    unesco: number;
    instagram: string;
    facebook: string;
};

export type OfficialLinkType = keyof OfficialLinkInputMap;

export interface OfficialLink<T = unknown> {
    text: string;
    Icon: Component;
    linkCreator: (param: T) => string;
}

export type OfficialLinkRegistry = {
    [K in OfficialLinkType]: OfficialLink<OfficialLinkInputMap[K]>;
};

export const OFFICIAL_LINKS: OfficialLinkRegistry = {
    officialWebsite: {
        text: 'Official Website',
        Icon: IconWeb,
        linkCreator: buildOfficialWebsiteLink
    },
    unesco: {
        text: 'Heritage UNESCO Site',
        Icon: IconEarth,
        linkCreator: buildUnescoLink
    },
    instagram: {
        text: 'Instagram',
        Icon: IconInstagram,
        linkCreator: buildInstagramLink
    },
    facebook: {
        text: 'Facebook',
        Icon: IconFacebook,
        linkCreator: buildFacebookLink
    }
};


export function generateOfficialLinks(data?: OfficialLinksParams | null): OfficialLinkBtnProps[] {
    if (!data) return [];

    const { websiteUrl, facebookId, instagramId, unescoId } = data;
    const links: OfficialLinkBtnProps[] = [];

    if (websiteUrl) {
        const item = OFFICIAL_LINKS.officialWebsite;
        links.push({ link: item.linkCreator(websiteUrl), Icon: item.Icon, text: item.text });
    }
    if (facebookId) {
        const item = OFFICIAL_LINKS.facebook;
        links.push({ link: item.linkCreator(facebookId), Icon: item.Icon, text: item.text });
    }
    if (instagramId) {
        const item = OFFICIAL_LINKS.instagram;
        links.push({ link: item.linkCreator(instagramId), Icon: item.Icon, text: item.text });
    }
    if (unescoId != null) {
        const item = OFFICIAL_LINKS.unesco;
        links.push({ link: item.linkCreator(unescoId), Icon: item.Icon, text: item.text });
    }

    return links;
}

export function buildOfficialWebsiteLink(websiteUrl: string, withRef = true): string {
    return withRef ? addRefParam(websiteUrl) : websiteUrl;
}

export function buildUnescoLink(unescoId: number, withRef = true): string {
    const url = `https://whc.unesco.org/en/list/${unescoId}`;
    return withRef ? addRefParam(url) : url;
}

export function buildInstagramLink(instagramId: string, withRef = true): string {
    const url = `https://instagram.com/${instagramId}`;
    return withRef ? addRefParam(url) : url;
}

export function buildFacebookLink(facebookId: string, withRef = true): string {
    const url = `https://facebook.com/${facebookId}`;
    return withRef ? addRefParam(url) : url;
}

function addRefParam(url: string): string {
    const ref = SHORT_SITE_URL;
    const parsed = new URL(url);
    parsed.searchParams.set('ref', ref);
    return parsed.toString();
}